import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Lock, Sparkles } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { useAchievements } from '@/hooks/useAchievements';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';

const CATEGORIES = [
  { id: 'wardrobe', label: 'Wardrobe', icon: 'Shirt' },
  { id: 'engagement', label: 'Streaks', icon: 'Flame' },
  { id: 'social', label: 'Social', icon: 'Users' },
  { id: 'special', label: 'Special', icon: 'Star' },
];

export function AchievementsGrid() {
  const { achievements, userAchievements, getProgress, getByCategory, getTotalPoints, loading } = useAchievements();

  const progress = useMemo(() => getProgress(), [getProgress]);
  const totalPoints = useMemo(() => getTotalPoints(), [getTotalPoints]);

  const getIcon = (iconName: string) => {
    const icons = LucideIcons as unknown as Record<string, React.ComponentType<{ size?: number; className?: string }>>;
    return icons[iconName] || Trophy;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Stats */}
      <div className="flex items-center justify-between p-4 rounded-2xl bg-gradient-to-r from-accent/10 to-primary/10 border border-accent/20">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-accent/20 flex items-center justify-center">
            <Trophy size={24} className="text-accent" />
          </div>
          <div>
            <p className="text-2xl font-bold">{progress.unlocked}</p>
            <p className="text-xs text-muted-foreground">
              of {progress.total} achievements
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-2xl font-bold text-accent">{totalPoints}</p>
          <p className="text-xs text-muted-foreground">Points Earned</p>
        </div>
      </div>

      {/* Progress Bar */}
      <div className="space-y-2">
        <div className="flex justify-between text-sm">
          <span>Progress</span>
          <span className="font-bold">{progress.percentage}%</span>
        </div>
        <Progress value={progress.percentage} className="h-3" />
      </div>

      {/* Category Tabs */}
      <Tabs defaultValue="wardrobe" className="w-full">
        <TabsList className="grid grid-cols-4 w-full">
          {CATEGORIES.map(cat => {
            const Icon = getIcon(cat.icon);
            return (
              <TabsTrigger key={cat.id} value={cat.id} className="gap-1 text-xs">
                <Icon size={14} />
                <span className="hidden sm:inline">{cat.label}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {CATEGORIES.map(cat => (
          <TabsContent key={cat.id} value={cat.id} className="mt-4">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {getByCategory(cat.id).map((achievement, index) => {
                const Icon = getIcon(achievement.icon);
                const isSecret = achievement.is_secret && !achievement.unlocked;

                return (
                  <motion.div
                    key={achievement.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className={`relative p-4 rounded-xl border text-center transition-all ${
                      achievement.unlocked
                        ? 'bg-gradient-to-b from-accent/10 to-transparent border-accent/30'
                        : 'bg-muted/30 border-border opacity-60'
                    }`}
                  >
                    {/* Icon */}
                    <div
                      className={`w-12 h-12 mx-auto mb-3 rounded-xl flex items-center justify-center ${
                        achievement.unlocked
                          ? 'bg-accent/20'
                          : 'bg-muted'
                      }`}
                    >
                      {isSecret ? (
                        <Lock size={24} className="text-muted-foreground" />
                      ) : (
                        <Icon
                          size={24}
                          className={achievement.unlocked ? 'text-accent' : 'text-muted-foreground'}
                        />
                      )}
                    </div>

                    {/* Name */}
                    <p className={`text-sm font-bold mb-1 ${
                      achievement.unlocked ? '' : 'text-muted-foreground'
                    }`}>
                      {isSecret ? '???' : achievement.name}
                    </p>

                    {/* Description */}
                    <p className="text-[10px] text-muted-foreground line-clamp-2">
                      {isSecret ? 'Complete to discover' : achievement.description}
                    </p>

                    {/* Points */}
                    <div className={`mt-2 inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-bold ${
                      achievement.unlocked
                        ? 'bg-accent/20 text-accent'
                        : 'bg-muted text-muted-foreground'
                    }`}>
                      <Sparkles size={10} />
                      {achievement.points} pts
                    </div>

                    {/* Unlocked Date */}
                    {achievement.unlocked && achievement.unlockedAt && (
                      <p className="text-[9px] text-muted-foreground mt-2">
                        {new Date(achievement.unlockedAt).toLocaleDateString()}
                      </p>
                    )}

                    {/* Unlocked badge */}
                    {achievement.unlocked && (
                      <div className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-green-500 flex items-center justify-center shadow-lg">
                        <LucideIcons.Check size={14} className="text-white" />
                      </div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </div>
  );
}
